import React, { useState } from "react";
import "./GridCommandEnhanced.scss";

/**
 * Enhanced Grid Command - Clean command center with game selector and multi-mission support
 */
const GridCommandEnhanced: React.FC = () => {
    const [selectedGame, setSelectedGame] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showGameSelector, setShowGameSelector] = useState(false);

    // Mock game data
    const games = [
        { id: "valorant", name: "Valorant", icon: "🎯", activeQuests: 5 },
        { id: "fortnite", name: "Fortnite", icon: "🏗️", activeQuests: 8 },
        { id: "apex", name: "Apex Legends", icon: "⚡", activeQuests: 4 },
        { id: "cod", name: "Call of Duty", icon: "🔫", activeQuests: 6 },
        { id: "overwatch", name: "Overwatch 2", icon: "🛡️", activeQuests: 3 },
    ];

    return (
        <div className="grid-command-enhanced">
            {/* Top Status Bar */}
            <div className="command-header">
                <div className="header-section">
                    <div className="status-indicator status-indicator--online" />
                    <span className="status-text">COMMAND CENTER ONLINE</span>
                </div>

                <div className="header-section">
                    <div className="sync-status">
                        <span className="sync-icon">🔄</span>
                        <span>LAST SYNC: 2 MIN AGO</span>
                    </div>
                </div>

                <div className="header-section">
                    <button className="notification-btn">
                        <span className="notification-icon">🔔</span>
                        <span className="notification-count">3</span>
                    </button>
                </div>
            </div>

            {/* Main Grid Layout */}
            <div className="command-grid">

                {/* LEFT: User Stats Panel */}
                <div className="panel panel--stats">
                    <div className="panel__header">
                        <h3>PILOT PROFILE</h3>
                        <span className="panel__id">#4287</span>
                    </div>

                    <div className="pilot-card">
                        <div className="pilot-avatar">
                            <div className="level-ring">
                                <svg viewBox="0 0 100 100">
                                    <circle cx="50" cy="50" r="45" className="ring-bg" />
                                    <circle cx="50" cy="50" r="45" className="ring-progress"
                                        strokeDasharray="283"
                                        strokeDashoffset="85" />
                                </svg>
                                <div className="level-number">42</div>
                            </div>
                        </div>

                        <div className="pilot-name">COMMANDER</div>

                        <div className="stat-grid">
                            <div className="stat-item">
                                <span className="stat-value">8,896</span>
                                <span className="stat-label">TOTAL XP</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">1,058</span>
                                <span className="stat-label">FUEL</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">67%</span>
                                <span className="stat-label">WIN RATE</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">12</span>
                                <span className="stat-label">DAY STREAK</span>
                            </div>
                        </div>

                        <div className="achievement-strip">
                            <div className="achievement achievement--new">
                                <span className="achievement-icon">🏆</span>
                                <span className="achievement-label">SHARPSHOOTER</span>
                            </div>
                            <div className="achievement">
                                <span className="achievement-icon">⚡</span>
                                <span className="achievement-label">SPEED DEMON</span>
                            </div>
                        </div>
                    </div>

                    <div className="xp-progression">
                        <div className="xp-header">
                            <span>NEXT LEVEL</span>
                            <span>2,104 XP</span>
                        </div>
                        <div className="xp-bar">
                            <div className="xp-fill" style={{width: "67%"}}>
                                <div className="xp-glow" />
                            </div>
                        </div>
                    </div>

                    <div className="guild-status">
                        <div className="guild-header">
                            <span className="guild-icon">👥</span>
                            <span className="guild-name">APEX PREDATORS</span>
                        </div>
                        <div className="guild-rank">
                            <span className="rank-label">GUILD RANK</span>
                            <span className="rank-value">#7</span>
                        </div>
                        <div className="guild-members">
                            <div className="member-avatars">
                                <div className="member-avatar member-avatar--online" />
                                <div className="member-avatar member-avatar--online" />
                                <div className="member-avatar member-avatar--away" />
                                <div className="member-avatar" />
                            </div>
                            <span className="member-count">38/50 ONLINE</span>
                        </div>
                    </div>
                </div>

                {/* MIDDLE: Mission Control */}
                <div className="panel panel--missions">
                    {/* Game Selector Bar */}
                    <div className="game-selector-bar">
                        <button
                            className={`game-filter-btn ${selectedGame === "all" ? "active" : ""}`}
                            onClick={() => setSelectedGame("all")}
                        >
                            <span className="filter-icon">🎮</span>
                            <span className="filter-label">ALL GAMES</span>
                            <span className="filter-count">26</span>
                        </button>

                        <div className="selected-game">
                            {selectedGame !== "all" &&
                <div className="game-tag">
                    <span className="game-icon">
                        {games.find(g => g.id === selectedGame)?.icon}
                    </span>
                    <span className="game-name">
                        {games.find(g => g.id === selectedGame)?.name}
                    </span>
                    <button
                        className="game-close"
                        onClick={() => setSelectedGame("all")}
                    >×</button>
                </div>
                            }
                        </div>

                        <button
                            className="game-search-btn"
                            onClick={() => setShowGameSelector(!showGameSelector)}
                        >
                            <span className="search-icon">🔍</span>
                            <span>FIND GAME</span>
                        </button>
                    </div>

                    {/* Game Selector Dropdown */}
                    {showGameSelector &&
            <div className="game-selector-dropdown">
                <input
                    type="text"
                    placeholder="Search games..."
                    className="game-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="game-list">
                    {games
                        .filter(game => game.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map(game =>
                            <button
                                key={game.id}
                                className={`game-option ${selectedGame === game.id ? "selected" : ""}`}
                                onClick={() => {
                                    setSelectedGame(game.id);
                                    setShowGameSelector(false);
                                    setSearchQuery("");
                                }}
                            >
                                <span className="game-icon">{game.icon}</span>
                                <span className="game-name">{game.name}</span>
                                <span className="quest-count">{game.activeQuests} quests</span>
                            </button>)}
                </div>
            </div>
                    }

                    {/* Mission Categories */}
                    <div className="mission-categories">
                        <div className="category-tab category-tab--active">
                            <span className="tab-label">GAME QUESTS</span>
                            <span className="tab-count">12</span>
                        </div>
                        <div className="category-tab">
                            <span className="tab-label">BRAND DROPS</span>
                            <span className="tab-count">5</span>
                            <span className="tab-indicator tab-indicator--new" />
                        </div>
                        <div className="category-tab">
                            <span className="tab-label">GUILD OPS</span>
                            <span className="tab-count">3</span>
                        </div>
                    </div>

                    {/* Active Missions */}
                    <div className="mission-grid">
                        {/* Featured Brand Mission */}
                        <div className="mission-card mission-card--brand">
                            <div className="brand-header">
                                <span className="brand-badge">🔥 BRAND DROP</span>
                                <span className="brand-sponsor">RED BULL</span>
                            </div>
                            <h3 className="mission-title">CLUTCH PLAYS CHALLENGE</h3>
                            <p className="mission-desc">Win 3 matches with less than 10% health remaining</p>
                            <div className="mission-games">
                                <span className="supported-games">Available in:</span>
                                <div className="game-tags">
                                    <span className="game-tag-mini">🎯</span>
                                    <span className="game-tag-mini">⚡</span>
                                    <span className="game-tag-mini">🔫</span>
                                </div>
                            </div>
                            <div className="mission-rewards">
                                <div className="reward">
                                    <span className="reward-amount">2,500</span>
                                    <span className="reward-type">XP</span>
                                </div>
                                <div className="reward">
                                    <span className="reward-amount">1,000</span>
                                    <span className="reward-type">FUEL</span>
                                </div>
                                <div className="reward reward--special">
                                    <span className="reward-icon">🎁</span>
                                    <span className="reward-type">EXCLUSIVE SKIN</span>
                                </div>
                            </div>
                            <div className="mission-timer">
                                <span className="timer-icon">⏱️</span>
                                <span className="timer-text">ENDS IN 2D 14H</span>
                            </div>
                            <button className="mission-action mission-action--primary">
                                START CHALLENGE
                            </button>
                        </div>

                        {/* Game-Specific Missions */}
                        <div className="mission-card">
                            <div className="mission-game">
                                <span className="game-icon">🎯</span>
                                <span className="game-name">VALORANT</span>
                            </div>
                            <h3 className="mission-title">ACE MACHINE</h3>
                            <p className="mission-desc">Get 5 ace rounds in competitive matches</p>
                            <div className="mission-progress">
                                <div className="progress-stats">
                                    <span>Progress</span>
                                    <span>2/5</span>
                                </div>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{width: "40%"}} />
                                </div>
                            </div>
                            <div className="mission-rewards">
                                <div className="reward">
                                    <span className="reward-amount">750</span>
                                    <span className="reward-type">XP</span>
                                </div>
                                <div className="reward">
                                    <span className="reward-amount">200</span>
                                    <span className="reward-type">FUEL</span>
                                </div>
                            </div>
                            <button className="mission-action">CONTINUE</button>
                        </div>

                        <div className="mission-card">
                            <div className="mission-game">
                                <span className="game-icon">🏗️</span>
                                <span className="game-name">FORTNITE</span>
                            </div>
                            <h3 className="mission-title">BUILDER PRO</h3>
                            <p className="mission-desc">Build 1000 structures in Battle Royale</p>
                            <div className="mission-progress">
                                <div className="progress-stats">
                                    <span>Progress</span>
                                    <span>847/1000</span>
                                </div>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{width: "84.7%"}} />
                                </div>
                            </div>
                            <div className="mission-rewards">
                                <div className="reward">
                                    <span className="reward-amount">500</span>
                                    <span className="reward-type">XP</span>
                                </div>
                            </div>
                            <button className="mission-action">CONTINUE</button>
                        </div>

                        {/* Daily Mission */}
                        <div className="mission-card mission-card--daily">
                            <div className="mission-type">
                                <span className="type-icon">📅</span>
                                <span className="type-label">DAILY QUEST</span>
                            </div>
                            <h3 className="mission-title">FIRST BLOOD</h3>
                            <p className="mission-desc">Get the first kill in any match</p>
                            <div className="mission-checklist">
                                <div className="checklist-item checklist-item--done">
                                    <span className="check-icon">✓</span>
                                    <span>Valorant</span>
                                </div>
                                <div className="checklist-item">
                                    <span className="check-icon">○</span>
                                    <span>Apex Legends</span>
                                </div>
                                <div className="checklist-item">
                                    <span className="check-icon">○</span>
                                    <span>Call of Duty</span>
                                </div>
                            </div>
                            <div className="mission-rewards">
                                <div className="reward">
                                    <span className="reward-amount">250</span>
                                    <span className="reward-type">XP</span>
                                </div>
                            </div>
                            <div className="mission-timer mission-timer--urgent">
                                <span className="timer-icon">⏱️</span>
                                <span className="timer-text">RESETS IN 4H</span>
                            </div>
                        </div>

                        {/* Guild Mission */}
                        <div className="mission-card mission-card--guild">
                            <div className="mission-type">
                                <span className="type-icon">👥</span>
                                <span className="type-label">GUILD OP</span>
                            </div>
                            <h3 className="mission-title">SQUAD SYNERGY</h3>
                            <p className="mission-desc">Win 10 matches with full guild squad</p>
                            <div className="guild-progress">
                                <div className="progress-stats">
                                    <span>Guild Progress</span>
                                    <span>7/10</span>
                                </div>
                                <div className="progress-bar">
                                    <div className="progress-fill progress-fill--guild" style={{width: "70%"}} />
                                </div>
                            </div>
                            <div className="contributors">
                                <span className="contributors-label">Top Contributors:</span>
                                <div className="contributor-list">
                                    <span className="contributor">xXSniperXx</span>
                                    <span className="contributor">ProGamer42</span>
                                    <span className="contributor">You</span>
                                </div>
                            </div>
                            <button className="mission-action mission-action--guild">JOIN SQUAD</button>
                        </div>
                    </div>
                </div>

                {/* RIGHT: Activity & Quick Actions */}
                <div className="panel-column">
                    {/* Activity Feed */}
                    <div className="panel panel--activity">
                        <div className="panel__header">
                            <h3>LIVE FEED</h3>
                            <button className="filter-btn">
                                <span>ALL</span>
                                <span className="dropdown-arrow">▼</span>
                            </button>
                        </div>

                        <div className="activity-list">
                            <div className="activity-item activity-item--achievement">
                                <div className="activity-icon">🏆</div>
                                <div className="activity-content">
                                    <div className="activity-text">Unlocked <strong>Sharpshooter</strong> badge</div>
                                    <div className="activity-time">2 min ago</div>
                                </div>
                            </div>

                            <div className="activity-item activity-item--quest">
                                <div className="activity-icon">✅</div>
                                <div className="activity-content">
                                    <div className="activity-text">Completed <strong>ACE MACHINE</strong> quest</div>
                                    <div className="activity-time">15 min ago</div>
                                </div>
                            </div>

                            <div className="activity-item activity-item--guild">
                                <div className="activity-icon">👥</div>
                                <div className="activity-content">
                                    <div className="activity-text">Guild ranked up to <strong>#6</strong></div>
                                    <div className="activity-time">1 hour ago</div>
                                </div>
                            </div>

                            <div className="activity-item activity-item--reward">
                                <div className="activity-icon">🎁</div>
                                <div className="activity-content">
                                    <div className="activity-text">Received <strong>500 FUEL</strong> reward</div>
                                    <div className="activity-time">2 hours ago</div>
                                </div>
                            </div>

                            <div className="activity-item activity-item--friend">
                                <div className="activity-icon">🎮</div>
                                <div className="activity-content">
                                    <div className="activity-text"><strong>DeadEye</strong> is now online</div>
                                    <div className="activity-time">3 hours ago</div>
                                </div>
                            </div>
                        </div>

                        <button className="view-all-btn">VIEW ALL ACTIVITY</button>
                    </div>

                    {/* Quick Actions */}
                    <div className="panel panel--actions">
                        <div className="panel__header">
                            <h3>QUICK ACTIONS</h3>
                        </div>

                        <div className="action-grid">
                            <button className="action-card action-card--primary">
                                <div className="action-icon">🎮</div>
                                <div className="action-label">PLAY NOW</div>
                                <div className="action-sublabel">Find Match</div>
                            </button>

                            <button className="action-card">
                                <div className="action-icon">🎁</div>
                                <div className="action-label">LOOT BOXES</div>
                                <div className="action-badge">2</div>
                            </button>

                            <button className="action-card">
                                <div className="action-icon">🛍️</div>
                                <div className="action-label">FUEL SHOP</div>
                            </button>

                            <button className="action-card">
                                <div className="action-icon">👥</div>
                                <div className="action-label">INVITE</div>
                            </button>
                        </div>
                    </div>

                    {/* Upcoming Events */}
                    <div className="panel panel--events">
                        <div className="panel__header">
                            <h3>UPCOMING</h3>
                        </div>

                        <div className="event-list">
                            <div className="event-item">
                                <div className="event-time">
                                    <span className="time-value">2H</span>
                                    <span className="time-label">14M</span>
                                </div>
                                <div className="event-info">
                                    <div className="event-name">Guild War</div>
                                    <div className="event-desc">Territory Takeover</div>
                                </div>
                            </div>

                            <div className="event-item">
                                <div className="event-time">
                                    <span className="time-value">8H</span>
                                    <span className="time-label">00M</span>
                                </div>
                                <div className="event-info">
                                    <div className="event-name">Double XP</div>
                                    <div className="event-desc">Weekend Event</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GridCommandEnhanced;